import React, { useEffect, useMemo, useState } from 'react';
import {
  Chart as ChartJS,
  BarController,
  BarElement,
  CategoryScale,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Chart } from 'react-chartjs-2';
import { useTheme } from '../../../theme';
import SectionCard, { SectionEmpty } from './SectionCard';
import { bucketLabel, fmtDuration } from './format';

ChartJS.register(BarController, BarElement, CategoryScale, LinearScale, LineController, LineElement, PointElement, Tooltip, Legend);

function readVar(name, fallback) {
  if (typeof window === 'undefined') return fallback;
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim() || fallback;
}

function useChartColors() {
  const { resolvedTheme } = useTheme();
  // data-theme is applied after render; re-read CSS variables on the following tick.
  const [tick, setTick] = useState(0);
  useEffect(() => setTick((n) => n + 1), [resolvedTheme]);
  return useMemo(
    () => ({
      primary: readVar('--primary', '#2563EB'),
      success: readVar('--success', '#10B981'),
      danger: readVar('--danger', '#EF4444'),
      muted: readVar('--text-muted', '#94A3B8'),
      grid: readVar('--chart-grid', '#E2E8F0'),
      tick: readVar('--chart-tick', '#94A3B8'),
      text: readVar('--text-secondary', '#475569'),
      tooltipBg: readVar('--chart-tooltip-bg', '#0F172A'),
      tooltipText: readVar('--chart-tooltip-text', '#F8FAFC'),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tick]
  );
}

function baseOptions(colors) {
  return {
    responsive: true,
    maintainAspectRatio: false,
    animation: { duration: 250 },
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: {
        position: 'top',
        align: 'end',
        labels: { usePointStyle: true, boxWidth: 8, boxHeight: 8, color: colors.text, font: { size: 11 } },
      },
      tooltip: {
        backgroundColor: colors.tooltipBg,
        titleColor: colors.tooltipText,
        bodyColor: colors.tooltipText,
        padding: 10,
        cornerRadius: 8,
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: colors.tick, font: { size: 11 }, maxRotation: 0, autoSkipPadding: 12 },
        border: { display: false },
      },
    },
  };
}

function hideTooltipOnScroll(chartRefs) {
  const hide = () =>
    chartRefs.forEach((ref) => {
      const chart = ref.current;
      if (chart?.tooltip?.getActiveElements?.().length) {
        chart.tooltip.setActiveElements([], { x: 0, y: 0 });
        chart.update('none');
      }
    });
  window.addEventListener('scroll', hide, { capture: true, passive: true });
  window.addEventListener('wheel', hide, { capture: true, passive: true });
  return () => {
    window.removeEventListener('scroll', hide, { capture: true });
    window.removeEventListener('wheel', hide, { capture: true });
  };
}

export default function QualityTrends({ summary, loading, error, onRetry }) {
  const colors = useChartColors();
  const trends = summary?.trends;
  const buckets = useMemo(() => trends?.buckets || [], [trends]);
  const labels = useMemo(() => buckets.map((b) => bucketLabel(b.key, trends?.granularity)), [buckets, trends]);
  const hasResults = buckets.some((b) => b.results > 0);
  const hasDurations = buckets.some((b) => b.avg_run_duration != null);
  const qualityRef = React.useRef(null);
  const durationRef = React.useRef(null);

  useEffect(() => hideTooltipOnScroll([qualityRef, durationRef]), []);

  const qualityData = useMemo(
    () => ({
      labels,
      datasets: [
        {
          type: 'bar',
          label: 'Passed',
          data: buckets.map((b) => b.passed),
          backgroundColor: colors.success,
          borderRadius: 3,
          stack: 'results',
          yAxisID: 'y',
          order: 2,
        },
        {
          type: 'bar',
          label: 'Failed',
          data: buckets.map((b) => b.failed),
          backgroundColor: colors.danger,
          borderRadius: 3,
          stack: 'results',
          yAxisID: 'y',
          order: 2,
        },
        {
          type: 'bar',
          label: 'Skipped',
          data: buckets.map((b) => b.skipped),
          backgroundColor: colors.muted,
          borderRadius: 3,
          stack: 'results',
          yAxisID: 'y',
          order: 2,
        },
        {
          type: 'line',
          label: 'Pass rate',
          data: buckets.map((b) => b.pass_rate),
          borderColor: colors.primary,
          backgroundColor: colors.primary,
          borderWidth: 2,
          pointRadius: 2.5,
          tension: 0.3,
          spanGaps: false,
          yAxisID: 'rate',
          order: 1,
        },
      ],
    }),
    [labels, buckets, colors]
  );

  const qualityOptions = useMemo(() => {
    const base = baseOptions(colors);
    return {
      ...base,
      plugins: {
        ...base.plugins,
        tooltip: {
          ...base.plugins.tooltip,
          callbacks: {
            label: (ctx) =>
              ctx.dataset.yAxisID === 'rate'
                ? ` Pass rate: ${ctx.parsed.y == null ? '—' : `${ctx.parsed.y}%`}`
                : ` ${ctx.dataset.label}: ${ctx.parsed.y}`,
          },
        },
      },
      scales: {
        ...base.scales,
        x: { ...base.scales.x, stacked: true },
        y: {
          stacked: true,
          beginAtZero: true,
          grid: { color: colors.grid },
          ticks: { color: colors.tick, font: { size: 11 }, precision: 0 },
          border: { display: false },
          title: { display: true, text: 'Results', color: colors.tick, font: { size: 11 } },
        },
        rate: {
          position: 'right',
          min: 0,
          max: 100,
          grid: { display: false },
          ticks: { color: colors.tick, font: { size: 11 }, callback: (v) => `${v}%` },
          border: { display: false },
        },
      },
    };
  }, [colors]);

  const durationData = useMemo(
    () => ({
      labels,
      datasets: [
        {
          type: 'line',
          label: 'Avg run duration',
          data: buckets.map((b) => b.avg_run_duration),
          borderColor: colors.primary,
          backgroundColor: colors.primary,
          borderWidth: 2,
          pointRadius: 2.5,
          tension: 0.3,
          spanGaps: true,
          yAxisID: 'y',
        },
        {
          type: 'bar',
          label: 'Runs started',
          data: buckets.map((b) => b.runs),
          backgroundColor: colors.grid,
          borderRadius: 3,
          yAxisID: 'runs',
        },
      ],
    }),
    [labels, buckets, colors]
  );

  const durationOptions = useMemo(() => {
    const base = baseOptions(colors);
    return {
      ...base,
      plugins: {
        ...base.plugins,
        tooltip: {
          ...base.plugins.tooltip,
          callbacks: {
            label: (ctx) =>
              ctx.dataset.yAxisID === 'y'
                ? ` Avg duration: ${ctx.parsed.y == null ? '—' : fmtDuration(ctx.parsed.y)}`
                : ` Runs started: ${ctx.parsed.y}`,
          },
        },
      },
      scales: {
        ...base.scales,
        y: {
          beginAtZero: true,
          grid: { color: colors.grid },
          ticks: { color: colors.tick, font: { size: 11 }, callback: (v) => fmtDuration(v) },
          border: { display: false },
        },
        runs: {
          position: 'right',
          beginAtZero: true,
          grid: { display: false },
          ticks: { color: colors.tick, font: { size: 11 }, precision: 0 },
          border: { display: false },
        },
      },
    };
  }, [colors]);

  const granularity = trends?.granularity === 'hour' ? 'Hourly' : 'Daily';

  return (
    <div className="pqd-grid pqd-grid--2">
      <SectionCard
        id="pqd-trend-quality"
        title="Pass rate & result volume"
        subtitle={`${granularity} buckets (UTC). Pass rate counts every pass/fail execution in the bucket.`}
        loading={loading}
        error={error}
        onRetry={onRetry}
        skeletonLines={6}
      >
        {hasResults ? (
          <div className="pqd-chart">
            <Chart ref={qualityRef} type="bar" data={qualityData} options={qualityOptions} />
          </div>
        ) : (
          <SectionEmpty title="No test results in this period">Change the time range or environment.</SectionEmpty>
        )}
      </SectionCard>
      <SectionCard
        id="pqd-trend-duration"
        title="Run duration"
        subtitle={`${granularity} average of finished top-level runs, with run volume.`}
        loading={loading}
        error={error}
        onRetry={onRetry}
        skeletonLines={6}
      >
        {hasDurations || buckets.some((b) => b.runs > 0) ? (
          <div className="pqd-chart">
            <Chart ref={durationRef} type="bar" data={durationData} options={durationOptions} />
          </div>
        ) : (
          <SectionEmpty title="No runs in this period">Durations appear once a test plan or suite run finishes.</SectionEmpty>
        )}
      </SectionCard>
    </div>
  );
}
